import Enumerable from "linq";
import {
  IPoint,
  ISize,
  ILineStyle,
  ITextureStyle,
  ITextAreaStyle,
  TextAlignKinds,
} from "../../../base/domain/drawing";
import { HisInformationSetting } from "../../../base/domain/his";
import { IClonable } from "../../../base/domain/system";
import { IPropertyPresentable, IPropertyEditable, PropertyPresenter, PropertyEditor } from "../../../base/domain/system/edit";
import {
  IPrintBehavior,
  IDateStyle,
  ITimeStyle,
  INumberStyle,
  ITurnedTextStyle,
  IFontSizeAutoAdjustBehavior,
  ILineBreakBehavior,
  IPageExtendBehavior,
  ICheckStyle,
  ICheckBehavior,
  IHisInformationItemDefaultSelectBehavior,
  HisInformationItemDefaultSelectKinds,
  IHospitalizedHisInformationItemSelectBehavior,
  IFreeInputBehavior,
  IInputRuleBehavior,
  IInputMethodBehavior,
  InputMethodKinds,
  IFocusBehavior,
  IQuoteBehavior,
  IEmptyAlertBehavior,
  IGroupAlertBehavior,
  PatientProfileCheckKinds,
  IRangeOfMotionBehavior,
  IItemGroupSelectBehavior,
} from "../../../documaker/domain/format/objects";
import { IFormatObjectEditable } from "./IFormatObjectEditable";
import {
  instanceOfILocationPropertyEditable,
  instanceOfISizePropertyEditable,
  IRectangleCoordinateEditable, instanceOfIRectangleCoordinateEditable,
  IPrintBehaviorPropertyEditable, instanceOfIPrintBehaviorPropertyEditable,
  ILineStylePropertyEditable, instanceOfILineStylePropertyEditable,
  ITextureStylePropertyEditable, instanceOfITextureStylePropertyEditable,
  ITextStylePropertyEditable, instanceOfITextStylePropertyEditable,
  ITextStyleSelectable, instanceOfITextStyleSelectable,
  IDateStylePropertyEditable, instanceOfIDateStylePropertyEditable,
  ITimeStylePropertyEditable, instanceOfITimeStylePropertyEditable,
  INumberStylePropertyEditable, instanceOfINumberStylePropertyEditable,
  ITurnedTextStylePropertyEditable, instanceOfITurnedTextStylePropertyEditable,
  IFontSizeAutoAdjustBehaviorPropertyEditable, instanceOfIFontSizeAutoAdjustBehaviorPropertyEditable,
  ILineBreakBehaviorPropertyEditable, instanceOfILineBreakBehaviorPropertyEditable,
  IPageExtendBehaviorPropertyEditable, instanceOfIPageExtendBehaviorPropertyEditable,
  ICheckStylePropertyEditable, instanceOfICheckStylePropertyEditable,
  ICheckBehaviorPropertyEditable, instanceOfICheckBehaviorPropertyEditable,
  IHisInformationItemDefaultSelectBehaviorPropertyEditable, instanceOfIHisInformationItemDefaultSelectBehaviorPropertyEditable,
  IHospitalizedHisInformationItemSelectBehaviorPropertyEditable, instanceOfIHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
  IFreeInputBehaviorPropertyEditable, instanceOfIFreeInputBehaviorPropertyEditable,
  IInputRuleBehaviorPropertyEditable, instanceOfIInputRuleBehaviorPropertyEditable,
  IInputMethodBehaviorPropertyEditable, instanceOfIInputMethodBehaviorPropertyEditable,
  IFocusBehaviorPropertyEditable, instanceOfIFocusBehaviorPropertyEditable,
  IQuoteBehaviorPropertyEditable, instanceOfIQuoteBehaviorPropertyEditable,
  IEmptyAlertBehaviorPropertyEditable, instanceOfIEmptyAlertBehaviorPropertyEditable,
  IGroupAlertBehaviorPropertyEditable, instanceOfIGroupAlertBehaviorPropertyEditable,
  IRangeOfMotionBehaviorPropertyEditable, instanceOfIRangeOfMotionBehaviorPropertyEditable,
  IItemGroupSelectBehaviorPropertyEditable, instanceOfIItemGroupSelectBehaviorPropertyEditable,
} from "./properties";

export class FormatObjectsEditor implements
IRectangleCoordinateEditable,
ILineStylePropertyEditable,
IPrintBehaviorPropertyEditable,
ITextureStylePropertyEditable,
ITextStylePropertyEditable,
ITextStyleSelectable,
IDateStylePropertyEditable,
ITimeStylePropertyEditable,
INumberStylePropertyEditable,
ITurnedTextStylePropertyEditable,
IFontSizeAutoAdjustBehaviorPropertyEditable,
ILineBreakBehaviorPropertyEditable,
IPageExtendBehaviorPropertyEditable,
ICheckStylePropertyEditable,
ICheckBehaviorPropertyEditable,
IHisInformationItemDefaultSelectBehaviorPropertyEditable,
IHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
IFreeInputBehaviorPropertyEditable,
IInputRuleBehaviorPropertyEditable,
IInputMethodBehaviorPropertyEditable,
IFocusBehaviorPropertyEditable,
IQuoteBehaviorPropertyEditable,
IEmptyAlertBehaviorPropertyEditable,
IGroupAlertBehaviorPropertyEditable,
IRangeOfMotionBehaviorPropertyEditable,
IItemGroupSelectBehaviorPropertyEditable,
IClonable<FormatObjectsEditor> {
  constructor(source: IFormatObjectEditable[] | FormatObjectsEditor) {
    this._source = (source instanceof FormatObjectsEditor)
      ? source._source
      : source;
    this._location = this.createPropertyEditor(
      instanceOfILocationPropertyEditable,
      s => s.location(),
      vs => ({
        x: this.toValueOrNull(vs, v => v.x),
        y: this.toValueOrNull(vs, v => v.y),
      }),
      (v, p, n) => ({
        x: this.toUpdatedValue(v, p, n, s => s.x),
        y: this.toUpdatedValue(v, p, n, s => s.y),
      }));
    this._size = this.createPropertyEditor(
      instanceOfISizePropertyEditable,
      s => s.size(),
      vs => ({
        width: this.toValueOrNull(vs, v => v.width),
        height: this.toValueOrNull(vs, v => v.height),
      }),
      (v, p, n) => ({
        width: this.toUpdatedValue(v, p, n, s => s.width),
        height: this.toUpdatedValue(v, p, n, s => s.height),
      }));
    this._center = this.createPropertyEditor(
      instanceOfIRectangleCoordinateEditable,
      s => s.center(),
      vs => ({
        x: this.toValueOrNull(vs, v => v.x),
        y: this.toValueOrNull(vs, v => v.y),
      }),
      (v, p, n) => ({
        x: this.toUpdatedValue(v, p, n, s => s.x),
        y: this.toUpdatedValue(v, p, n, s => s.y),
      }));
    this._bottomRight = this.createPropertyEditor(
      instanceOfIRectangleCoordinateEditable,
      s => s.bottomRight(),
      vs => ({
        x: this.toValueOrNull(vs, v => v.x),
        y: this.toValueOrNull(vs, v => v.y),
      }),
      (v, p, n) => ({
        x: this.toUpdatedValue(v, p, n, s => s.x),
        y: this.toUpdatedValue(v, p, n, s => s.y),
      }));
    this._printBehavior = this.createPropertyEditor(
      instanceOfIPrintBehaviorPropertyEditable,
      s => s.printBehavior(),
      vs => ({
        shouldPrint: this.toValueOrNull(vs, v => v.shouldPrint),
        shouldPreview: this.toValueOrNull(vs, v => v.shouldPreview),
      }),
      (v, p, n) => ({
        shouldPrint: this.toUpdatedValue(v, p, n, s => s.shouldPrint),
        shouldPreview: this.toUpdatedValue(v, p, n, s => s.shouldPreview),
      }));
    this._lineStyle = this.createPropertyEditor(
      instanceOfILineStylePropertyEditable,
      s => s.lineStyle(),
      vs => ({        // 全て同じ値じゃない場合はnullにする.
        kind: this.toValueOrNull(vs, v => v.kind),
        color: {
          value: this.toValueOrNull(vs, v => v.color.value),
        },
        width: this.toValueOrNull(vs, v => v.width),
        opacity: 1,
      }),
      (v, p, n) => ({ // 変化してないデータは更新されたくないので更新する値を作成.
        kind: this.toUpdatedValue(v, p, n, s => s.kind),
        color: {
          value: this.toUpdatedValue(v, p, n, s => s.color.value),
        },
        width: this.toUpdatedValue(v, p, n, s => s.width),
        opacity: 1,
      }));
    this._textureStyle = this.createPropertyEditor(
      instanceOfITextureStylePropertyEditable,
      s => s.textureStyle(),
      vs => ({
        kind: this.toValueOrNull(vs, v => v.kind),
      }),
      (v, p, n) => ({
        kind: this.toUpdatedValue(v, p, n, s => s.kind),
      }));
    this._textStyle = this.createPropertyEditor(
      instanceOfITextStylePropertyEditable,
      s => s.textStyle(),
      (vs, ss) => ({
        fontStyle: {
          color: {
            value: this.toValueOrNull(
              Enumerable.from(ss)
                .where(s => !instanceOfITextStyleSelectable(s.source) || !s.source.isFontColorSelectable())
                .select(s => s.value)
                .toArray(),
              v => v.fontStyle.color.value),
          },
          font: {
            family: this.toValueOrNull(vs, v => v.fontStyle.font.family),
            size: this.toValueOrNull(vs, v => v.fontStyle.font.size),
          }
        },
        paintStyle: {
          isEnable: this.toValueOrNull(
            Enumerable.from(ss)
              .where(s => !instanceOfITextStyleSelectable(s.source) || !s.source.isPaintColorSelectable())
              .select(s => s.value)
              .toArray(),
            v => v.paintStyle.isEnable),
          color: {
            value: this.toValueOrNull(
              Enumerable.from(ss)
                .where(s => !instanceOfITextStyleSelectable(s.source) || !s.source.isPaintColorSelectable())
                .select(s => s.value)
                .toArray(),
              v => v.paintStyle.color.value),
          },
        },
        alignment: this.toValueOrNull(vs, v => v.alignment),
      }),
      (v, p, n, e) => ({
        fontStyle: {
          color: {
            value: this.toUpdatedValue(v, p, !instanceOfITextStyleSelectable(e) || !e.isFontColorSelectable() ? n : p, s => s.fontStyle.color.value),
          },
          font: {
            family: this.toUpdatedValue(v, p, n, s => s.fontStyle.font.family),
            size: this.toUpdatedValue(v, p, n, s => s.fontStyle.font.size),
          }
        },
        paintStyle: {
          isEnable: this.toUpdatedValue(v, p, !instanceOfITextStyleSelectable(e) || !e.isPaintColorSelectable() ? n : p, s => s.paintStyle.isEnable),
          color: {
            value: this.toUpdatedValue(v, p, !instanceOfITextStyleSelectable(e) || !e.isPaintColorSelectable() ? n : p, s => s.paintStyle.color.value),
          },
        },
        alignment: this.toUpdatedValue(v, p, (e.getSelectableAlignmentKinds().indexOf(n.alignment) < 0) ? p : n, s => s.alignment),
      }));
    this._dateStyle = this.createPropertyEditor(
      instanceOfIDateStylePropertyEditable,
      s => s.dateStyle(),
      vs => ({
        formatKind: this.toValueOrNull(vs, v => v.formatKind),
        hasWeekDay: this.toValueOrNull(vs, v => v.hasWeekDay),
      }),
      (v, p, n) => ({
        formatKind: this.toUpdatedValue(v, p, n, s => s.formatKind),
        hasWeekDay: this.toUpdatedValue(v, p, n, s => s.hasWeekDay),
      }));
    this._timeStyle = this.createPropertyEditor(
      instanceOfITimeStylePropertyEditable,
      s => s.timeStyle(),
      vs => ({
        formatKind: this.toValueOrNull(vs, v => v.formatKind),
      }),
      (v, p, n) => ({
        formatKind: this.toUpdatedValue(v, p, n, s => s.formatKind),
      }));
    this._numberStyle = this.createPropertyEditor(
      instanceOfINumberStylePropertyEditable,
      s => s.numberStyle(),
      vs => ({
        formatKind: this.toValueOrNull(vs, v => v.formatKind),
      }),
      (v, p, n) => ({
        formatKind: this.toUpdatedValue(v, p, n, s => s.formatKind),
      }));
    this._turnedTextStyle = this.createPropertyEditor(
      instanceOfITurnedTextStylePropertyEditable,
      s => s.turnedTextStyle(),
      vs => ({
        formatKind: this.toValueOrNull(vs, v => v.formatKind),
      }),
      (v, p, n) => ({
        formatKind: this.toUpdatedValue(v, p, n, s => s.formatKind),
      }));
    this._fontSizeAutoAdjustBehavior = this.createPropertyEditor(
      instanceOfIFontSizeAutoAdjustBehaviorPropertyEditable,
      s => s.fontSizeAutoAdjustBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
      }));
    this._lineBreakBehavior = this.createPropertyEditor(
      instanceOfILineBreakBehaviorPropertyEditable,
      s => s.lineBreakBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
      }));
    this._pageExtendBehavior = this.createPropertyEditor(
      instanceOfIPageExtendBehaviorPropertyEditable,
      s => s.pageExtendBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
        fontSizeThreshold: this.toValueOrNull(vs, v => v.fontSizeThreshold),
        shouldMoveAllText: this.toValueOrNull(vs, v => v.shouldMoveAllText),
        shouldOverwrite: this.toValueOrNull(vs, v => v.shouldOverwrite),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
        fontSizeThreshold: this.toUpdatedValue(v, p, n, s => s.fontSizeThreshold),
        shouldMoveAllText: this.toUpdatedValue(v, p, n, s => s.shouldMoveAllText),
        shouldOverwrite: this.toUpdatedValue(v, p, n, s => s.shouldOverwrite),
      }));
    this._checkStyle = this.createPropertyEditor(
      instanceOfICheckStylePropertyEditable,
      s => s.checkStyle(),
      vs => ({        // 全て同じ値じゃない場合はnullにする.
        kind: this.toValueOrNull(vs, v => v.kind),
        paint: {
          isEnable: this.toValueOrNull(vs, v => v.paint.isEnable),
          color: {
            value: this.toValueOrNull(vs, v => v.paint.color.value),
          },
        },
        border: {
          isEnable: this.toValueOrNull(vs, v => v.border.isEnable),
        },
      }),
      (v, p, n) => ({ // 変化してないデータは更新されたくないので更新する値を作成.
        kind: this.toUpdatedValue(v, p, n, s => s.kind),
        paint: {
          isEnable: this.toUpdatedValue(v, p, n, s => s.paint.isEnable),
          color: {
            value: this.toUpdatedValue(v, p, n, s => s.paint.color.value),
          },
        },
        border: {
          isEnable: this.toUpdatedValue(v, p, n, s => s.border.isEnable),
        },
      }));
    this._checkBehavior = this.createPropertyEditor(
      instanceOfICheckBehaviorPropertyEditable,
      s => s.checkBehavior(),
      vs => ({        // 全て同じ値じゃない場合はnullにする.
        isChecked: this.toValueOrNull(vs, v => v.isChecked),
        isMultiCheckable: this.toValueOrNull(vs, v => v.isMultiCheckable),
        score: {
          on: this.toValueOrNull(vs, v => v.score.on),
          off: this.toValueOrNull(vs, v => v.score.off),
        },
        patientProfileKinds: this.toValueOrNull(vs, v => v.patientProfileKinds),
        pastedTextToCheck: this.toValueOrNull(vs, v => v.pastedTextToCheck),
        isGroupScopePage: this.toValueOrNull(vs, v => v.isGroupScopePage),
      }),
      (v, p, n, e) => ({ // 変化してないデータは更新されたくないので更新する値を作成.
        isChecked: this.toUpdatedValue(v, p, n, s => s.isChecked),
        isMultiCheckable: this.toUpdatedValue(v, p, n, s => s.isMultiCheckable),
        score: {
          on: this.toUpdatedValue(v, p, n, s => s.score.on),
          off: this.toUpdatedValue(v, p, n, s => s.score.off),
        },
        patientProfileKinds: this.toUpdatedValue(v, p, (e.getSelectablePatientProfileCheckKinds().indexOf(n.patientProfileKinds) < 0) ? p : n, s => s.patientProfileKinds),
        pastedTextToCheck: this.toUpdatedValue(v, p, n, s => s.pastedTextToCheck),
        isGroupScopePage: this.toUpdatedValue(v, p, n, s => s.isGroupScopePage),
      }));
    this._hisInformationKey = this.createDummyPropertyEditor(""); // hisInformationItemDefaultSelectBehaviorに必要なので編集不可で定義.
    this._hisInformationItemDefaultSelectBehavior = this.createPropertyEditor(
      instanceOfIHisInformationItemDefaultSelectBehaviorPropertyEditable,
      s => s.hisInformationItemDefaultSelectBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
        kind: this.toValueOrNull(vs, v => v.kind),
      }),
      (v, p, n, e) => ({  // kindのCreatedDateは日付オブジェクト以外更新しない
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
        kind: this.toUpdatedValue(v, p, (e.getSelectableHisInformationItemDefaultSelectKinds().indexOf(n.kind) < 0) ? p : n, s => s.kind),
      }));
    this._hospitalizedHisInformationItemSelectBehavior = this.createPropertyEditor(
      instanceOfIHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
      s => s.hospitalizedHisInformationItemSelectBehavior(),
      vs => ({
        shouldSelectHospitalizedOnly: this.toValueOrNull(vs, v => v.shouldSelectHospitalizedOnly),
        baseDateReferBehavior: {
          isEnable: this.toValueOrNull(vs, v => v.baseDateReferBehavior.isEnable),
          reference: this.toValueOrNull(vs, v => v.baseDateReferBehavior.reference),
        },
      }),
      (v, p, n) => ({
        shouldSelectHospitalizedOnly: this.toUpdatedValue(v, p, n, s => s.shouldSelectHospitalizedOnly),
        baseDateReferBehavior: {
          isEnable: this.toUpdatedValue(v, p, n, s => s.baseDateReferBehavior.isEnable),
          reference: this.toUpdatedValue(v, p, n, s => s.baseDateReferBehavior.reference),
        },
      }));
    this._freeInputBehavior = this.createPropertyEditor(
      instanceOfIFreeInputBehaviorPropertyEditable,
      s => s.freeInputBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
      }));
    this._inputRuleBehavior = this.createPropertyEditor(
      instanceOfIInputRuleBehaviorPropertyEditable,
      s => s.inputRuleBehavior(),
      vs => ({
        kind: this.toValueOrNull(vs, v => v.kind),
      }),
      (v, p, n) => ({
        kind: this.toUpdatedValue(v, p, n, s => s.kind),
      }));
    this._inputMethodBehavior = this.createPropertyEditor(
      instanceOfIInputMethodBehaviorPropertyEditable,
      s => s.inputMethodBehavior(),
      vs => ({
        kind: this.toValueOrNull(vs, v => v.kind),
      }),
      (v, p, n, e) => ({
        kind: this.toUpdatedValue(v, p, (e.getSelectableInputMethods().indexOf(n.kind) < 0) ? p : n, s => s.kind),
      }));
    this._focusBehavior = this.createPropertyEditor(
      instanceOfIFocusBehaviorPropertyEditable,
      s => s.focusBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
      }));
    this._quoteBehavior = this.createPropertyEditor(
      instanceOfIQuoteBehaviorPropertyEditable,
      s => s.quoteBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
      }));
    this._emptyAlertBehavior = this.createPropertyEditor(
      instanceOfIEmptyAlertBehaviorPropertyEditable,
      s => s.emptyAlertBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
      }));
    this._groupAlertBehavior = this.createPropertyEditor(
      instanceOfIGroupAlertBehaviorPropertyEditable,
      s => s.groupAlertBehavior(),
      vs => ({
        isEnable: this.toValueOrNull(vs, v => v.isEnable),
        name: this.toValueOrNull(vs, v => v.name),
      }),
      (v, p, n) => ({
        isEnable: this.toUpdatedValue(v, p, n, s => s.isEnable),
        name: this.toUpdatedValue(v, p, n, s => s.name),
      }));
    this._rangeOfMotionBehavior = this.createPropertyEditor(
      instanceOfIRangeOfMotionBehaviorPropertyEditable,
      s => s.rangeOfMotionBehavior(),
      vs => ({
        range: {
          min: this.toValueOrNull(vs, v => v.range.min),
          max: this.toValueOrNull(vs, v => v.range.max),
        },
        manualMuscleTestBahavior: {
          inputMethod: this.toValueOrNull(vs, v => v.manualMuscleTestBahavior.inputMethod),
          presentMethod: this.toValueOrNull(vs, v => v.manualMuscleTestBahavior.presentMethod),
        }, 
      }),
      (v, p, n) => ({
        range: {
          min: this.toUpdatedValue(v, p, n, s => s.range.min),
          max: this.toUpdatedValue(v, p, n, s => s.range.max),
        },
        manualMuscleTestBahavior: {
          inputMethod: this.toUpdatedValue(v, p, n, s => s.manualMuscleTestBahavior.inputMethod),
          presentMethod: this.toUpdatedValue(v, p, n, s => s.manualMuscleTestBahavior.presentMethod),
        },
      }));
    this._ItemGroupSelectBehavior = this.createPropertyEditor(
      instanceOfIItemGroupSelectBehaviorPropertyEditable,
      s => s.itemGroupSelectBehavior(),
      vs => ({
        groupName: this.toValueOrNull(vs, v => v.groupName),
      }),
      (v, p, n) => ({
        groupName: this.toUpdatedValue(v, p, n, s => s.groupName),
      }));
  }

  private toValueOrNull<TSource, TValue>(source: TSource[], toValue: (s: TSource) => TValue): TValue {
    if (!source || (source.length <= 0)) {
      return null;
    }
    const value = toValue(source[0]);
    return Enumerable.from(source).any(s => toValue(s) !== toValue(source[0]))
      ? null
      : value;
  }

  private toUpdatedValue<TSource, TValue>(source: TSource, original: TSource, updated: TSource, toValue: (s: TSource) => TValue): TValue {
    return (toValue(original) !== toValue(updated))
      ? toValue(updated)
      : toValue(source);
  }

  private readonly _source: IFormatObjectEditable[];
  private _location: IPropertyEditable<IPoint>;
  private _size: IPropertyEditable<ISize>;
  private _center: IPropertyEditable<IPoint>;
  private _bottomRight: IPropertyEditable<IPoint>;
  private _printBehavior: IPropertyEditable<IPrintBehavior>;
  private _lineStyle: IPropertyEditable<ILineStyle>;
  private _textureStyle: IPropertyEditable<ITextureStyle>;
  private _textStyle: IPropertyEditable<ITextAreaStyle>;
  private _dateStyle: IPropertyEditable<IDateStyle>;
  private _timeStyle: IPropertyEditable<ITimeStyle>;
  private _numberStyle: IPropertyEditable<INumberStyle>;
  private _turnedTextStyle: IPropertyEditable<ITurnedTextStyle>;
  private _fontSizeAutoAdjustBehavior: IPropertyEditable<IFontSizeAutoAdjustBehavior>;
  private _lineBreakBehavior: IPropertyEditable<ILineBreakBehavior>;
  private _pageExtendBehavior: IPropertyEditable<IPageExtendBehavior>;
  private _checkStyle: IPropertyEditable<ICheckStyle>;
  private _checkBehavior: IPropertyEditable<ICheckBehavior>;
  private _hisInformationKey: IPropertyEditable<string>;
  private _hisInformationItemDefaultSelectBehavior: IPropertyEditable<IHisInformationItemDefaultSelectBehavior>;
  private _hospitalizedHisInformationItemSelectBehavior: IPropertyEditable<IHospitalizedHisInformationItemSelectBehavior>;
  private _freeInputBehavior: IPropertyEditable<IFreeInputBehavior>;
  private _inputRuleBehavior: IPropertyEditable<IInputRuleBehavior>;
  private _inputMethodBehavior: IPropertyEditable<IInputMethodBehavior>;
  private _focusBehavior: IPropertyEditable<IFocusBehavior>;
  private _quoteBehavior: IPropertyEditable<IQuoteBehavior>;
  private _emptyAlertBehavior: IPropertyEditable<IEmptyAlertBehavior>;
  private _groupAlertBehavior: IPropertyEditable<IGroupAlertBehavior>;
  private _rangeOfMotionBehavior: IPropertyEditable<IRangeOfMotionBehavior>;
  private _ItemGroupSelectBehavior: IPropertyEditable<IItemGroupSelectBehavior>;

  public source(): IFormatObjectEditable[] {
    return this._source;
  }

  location(): IPropertyEditable<IPoint> {
    return this._location;
  }

  size(): IPropertyEditable<ISize> {
    return this._size;
  }

  topLeft(): IPropertyEditable<IPoint> {
    return this._location;
  }

  center(): IPropertyEditable<IPoint> {
    return this._center;
  }

  bottomRight(): IPropertyEditable<IPoint> {
    return this._bottomRight;
  }

  printBehavior(): IPropertyEditable<IPrintBehavior> {
    return this._printBehavior;
  }

  lineStyle(): IPropertyEditable<ILineStyle> {
    return this._lineStyle;
  }

  textureStyle(): IPropertyEditable<ITextureStyle> {
    return this._textureStyle;
  }

  textStyle(): IPropertyEditable<ITextAreaStyle> {
    return this._textStyle;
  }

  getSelectableAlignmentKinds(): TextAlignKinds[] {
    return this.extractSelectableOptions(
      instanceOfITextStylePropertyEditable,
      s => s.getSelectableAlignmentKinds());
  }

  isFontColorSelectable(): boolean {
    // 全てが選択可能な場合のみ選択可能とする.(フォント色選択UIが非表示になる.)
    return Enumerable.from(this._source)
      .all(s => instanceOfITextStyleSelectable(s) && s.isFontColorSelectable());
  }

  isPaintColorSelectable(): boolean {
    // 全てが選択可能な場合のみ選択可能とする.(背景色選択UIが非表示になる.)
    return Enumerable.from(this._source)
      .all(s => instanceOfITextStyleSelectable(s) && s.isPaintColorSelectable());
  }

  dateStyle(): IPropertyEditable<IDateStyle> {
    return this._dateStyle;
  }

  timeStyle(): IPropertyEditable<ITimeStyle> {
    return this._timeStyle;
  }

  numberStyle(): IPropertyEditable<INumberStyle> {
    return this._numberStyle;
  }

  turnedTextStyle(): IPropertyEditable<ITurnedTextStyle> {
    return this._turnedTextStyle;
  }

  fontSizeAutoAdjustBehavior(): IPropertyEditable<IFontSizeAutoAdjustBehavior> {
    return this._fontSizeAutoAdjustBehavior;
  }

  lineBreakBehavior(): IPropertyEditable<ILineBreakBehavior> {
    return this._lineBreakBehavior;
  }

  pageExtendBehavior(): IPropertyEditable<IPageExtendBehavior> {
    return this._pageExtendBehavior;
  }

  checkStyle(): IPropertyEditable<ICheckStyle> {
    return this._checkStyle;
  }

  checkBehavior(): IPropertyEditable<ICheckBehavior> {
    return this._checkBehavior;
  }

  isPatientProfileCheckKindSelectable(): boolean {
    return Enumerable.from(this._source)
      .any(s => instanceOfICheckBehaviorPropertyEditable(s) && s.isPatientProfileCheckKindSelectable());
  }

  getSelectablePatientProfileCheckKinds(): PatientProfileCheckKinds[] {
    return this.extractSelectableOptions(
      instanceOfICheckBehaviorPropertyEditable,
      s => s.getSelectablePatientProfileCheckKinds());
  }

  isGroupScopeSelectable(): boolean {
    return Enumerable.from(this._source)
      .any(s => instanceOfICheckBehaviorPropertyEditable(s) && s.isGroupScopeSelectable());
  }

  hisInformationKey(): IPropertyEditable<string> {
    return this._hisInformationKey;
  }

  getHisInformationName(): string {
    return "";    // hisInformationItemDefaultSelectBehaviorのための編集不可プロパティなので仮の値を返す.
  }

  getSelectableHisInformations(): HisInformationSetting[] {
    return [];    // hisInformationItemDefaultSelectBehaviorのための編集不可プロパティなので仮の値を返す.
  }

  getSelectedHisInformation(): HisInformationSetting {
    return null;  // hisInformationItemDefaultSelectBehaviorのための編集不可プロパティなので仮の値を返す.
  }

  isHisInformationMixable(): boolean {
    return false; // hisInformationItemDefaultSelectBehaviorのための編集不可プロパティなので仮の値を返す.
  }

  hisInformationItemDefaultSelectBehavior(): IPropertyEditable<IHisInformationItemDefaultSelectBehavior> {
    return this._hisInformationItemDefaultSelectBehavior;
  }

  getSelectableHisInformationItemDefaultSelectKinds(): HisInformationItemDefaultSelectKinds[] {
    return this.extractSelectableOptions(
      instanceOfIHisInformationItemDefaultSelectBehaviorPropertyEditable,
      s => s.getSelectableHisInformationItemDefaultSelectKinds());
  }

  hospitalizedHisInformationItemSelectBehavior(): IPropertyEditable<IHospitalizedHisInformationItemSelectBehavior> {
    return this._hospitalizedHisInformationItemSelectBehavior;
  }

  freeInputBehavior(): IPropertyEditable<IFreeInputBehavior> {
    return this._freeInputBehavior;
  }

  inputRuleBehavior(): IPropertyEditable<IInputRuleBehavior> {
    return this._inputRuleBehavior;
  }

  inputMethodBehavior(): IPropertyEditable<IInputMethodBehavior> {
    return this._inputMethodBehavior;
  }

  getSelectableInputMethods(): InputMethodKinds[] {
    return this.extractSelectableOptions(
      instanceOfIInputMethodBehaviorPropertyEditable,
      s => s.getSelectableInputMethods());
  }

  focusBehavior(): IPropertyEditable<IFocusBehavior> {
    return this._focusBehavior;
  }

  quoteBehavior(): IPropertyEditable<IQuoteBehavior> {
    return this._quoteBehavior;
  }

  emptyAlertBehavior(): IPropertyEditable<IEmptyAlertBehavior> {
    return this._emptyAlertBehavior;
  }

  groupAlertBehavior(): IPropertyEditable<IGroupAlertBehavior> {
    return this._groupAlertBehavior;
  }

  rangeOfMotionBehavior(): IPropertyEditable<IRangeOfMotionBehavior> {
    return this._rangeOfMotionBehavior;
  }
  
  itemGroupSelectBehavior(): IPropertyEditable<IItemGroupSelectBehavior> {
    return this._ItemGroupSelectBehavior;
  }

  protected extractSelectableOptions<T, TEditor>(isEditor: (s: any) => s is TEditor, toArray: (source: TEditor) => T[]): T[] {
    // 選択肢は和集合で生成する.(積集合だと選びたいものを選べないので.選んだ結果設定できないものは設定側で弾く.)
    const arrays = Enumerable.from(this._source)
      .select(e => isEditor(e) ? toArray(e) : null)
      .where(a => !!a)
      .toArray();
    return Enumerable.from(arrays)
      .selectMany(a => a)
      .groupBy(v => v)
      .select(g => g.key())
      .toArray();
  }

  protected createPropertyPresenter<T>(get: (s: IFormatObjectEditable[]) => T): IPropertyPresentable<T> {
    return new PropertyPresenter({
      source: this._source,
      read: s => get(s),
    });
  }

  protected createPropertyEditor<T, TEditor>(
    isEditor: (s: any) => s is TEditor,
    toEditor: (s: TEditor) => IPropertyEditable<T>,
    toPreviousValue?: (vs: T[], ss: { source: (IFormatObjectEditable & TEditor), value: T }[]) => T,
    toValue?: (v: T, p: T, n: T, s: IFormatObjectEditable & TEditor) => T): IPropertyEditable<T> {
    const read = (s: IFormatObjectEditable[]) => {
      const editores = Enumerable.from(s)
        .select(e => isEditor(e) ? { editor: toEditor(e), source: e } : null)
        .where(e => !!e && e.editor.isEditable());
      const values = editores.select(e => e.editor.getValue()).toArray();
      const sources = editores.select(e => ({ source: e.source, value: e.editor.getValue() })).toArray();
      return (0 < values.length)
        ? (toPreviousValue ? toPreviousValue(values, sources) : this.toValueOrNull(values, v => v))
        : null;
    };
    return new PropertyEditor({
      source: this._source,
      read,
      write: v => {
        for (const e of this._source) {
          if (isEditor(e)) {
            const editor = toEditor(e);
            if (editor.isEditable()) {
              editor.setValue(toValue ? toValue(editor.getValue(), read(this._source), v, e) : v);
            }
          }
        }
      },
      isWritable: s => Enumerable.from(s).any(e => isEditor(e) && toEditor(e).isEditable()),
    });
  }

  protected createDummyPropertyEditor<T>(value: T): IPropertyEditable<T> {
    return {
      isEditable: () => false,
      getValue: () => value,
      setValue: (v: T) => {},
    };
  }

  getTypeName(): string {
    return this.source().length + "オブジェクト選択中";
  }

  isAlreadyLayouted(): boolean {
    return true;
  }

  clone(): FormatObjectsEditor {
    return new FormatObjectsEditor(this);
  }
}
